import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { AvatarUpdateRequest, LogoUpdateRequest } from '../../../../../api';
import { BaseAvatarSize } from '../../../../shared/components/avatar/base-avatar.component';
import { baseAdministrationActions, baseAdministrationSelectors, BaseCoreState } from '../../../../_store';
import { BaseRolesService } from '../../../../core/services/base-roles.service';
import { TabWithPermissionsInterface } from '../../../../shared/interfaces/base-tab-with-permissions.interface';

@Component({
  selector: 'base-administration',
  templateUrl: './base-administration.component.html',
})
export class BaseAdministrationComponent implements OnInit, OnDestroy {
  @Input() tabs: Array<TabWithPermissionsInterface>;
  @Input() isCustomer = false;

  avatarSize = BaseAvatarSize.large;
  currentTab: TabWithPermissionsInterface;
  isMyProfile = false;
  isCustomerLogoEditable = false;

  private destroy$ = new Subject<boolean>();
  private signDigital: boolean | undefined;

  constructor(
    private store: Store<BaseCoreState>,
    private router: Router,
    private baseRolesService: BaseRolesService
  ) {}

  ngOnInit(): void {
    this.isCustomerLogoEditable = this.baseRolesService.hasAdminRole();

    this.setCurrentTab(this.getTabByUrl(this.router.url));
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
      this.setCurrentTab(this.getTabByUrl(event.url));
    });

    if (this.baseRolesService.isPsp()) {
      this.store
        .select(baseAdministrationSelectors.getPspData)
        .pipe(
          takeUntil(this.destroy$),
          filter((state) => !!state.ok),
          map((state) => state.data)
        )
        .subscribe((value) => {
          if (value) {
            this.signDigital = value.signDigital;
            this.filterTabsBasedOnSignDigital();
          }
        });
    } else {
      this.store
        .select(baseAdministrationSelectors.getCustomerData)
        .pipe(
          takeUntil(this.destroy$),
          filter((state) => !!state.ok),
          map((state) => state.data)
        )
        .subscribe((value) => {
          if (value) {
            this.signDigital = value.signDigital;
            this.filterTabsBasedOnSignDigital();
          }
        });
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  onUpdateAvatar(body: AvatarUpdateRequest | LogoUpdateRequest): void {
    if (!this.isCustomerLogoEditable && !this.isMyProfile) {
      return;
    }
    this.isMyProfile
      ? this.store.dispatch(
          baseAdministrationActions.updateProfileAvatarAction({ payload: { body: body as AvatarUpdateRequest } })
        )
      : this.isCustomer
      ? this.store.dispatch(
          baseAdministrationActions.updateCustomerLogoAction({ payload: { body: body as LogoUpdateRequest } })
        )
      : this.store.dispatch(
          baseAdministrationActions.updatePspLogoAction({ payload: { body: body as LogoUpdateRequest } })
        );
  }

  onTabChange(tab: TabWithPermissionsInterface) {
    this.setCurrentTab(tab);
  }

  private filterTabsBasedOnSignDigital() {
    if (this.signDigital) {
      return;
    }

    this.tabs = this.tabs.filter((tab) => tab.id !== 60);
  }

  private getTabByUrl(url: string): TabWithPermissionsInterface {
    return this.tabs.find((tab) => url.includes(tab.url)) || this.tabs[0];
  }

  private setCurrentTab(tab: TabWithPermissionsInterface): void {
    this.isMyProfile = tab.url.includes('user-settings');
    this.currentTab = tab;
  }
}
